import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import ReelOutlineChatInterface from './ReelOutlineChatInterface';
import { useReelOutlineGeneration } from './useReelOutlineGeneration';

import type { ReelOutline, ReelProducerConfig } from './types';

const logger = LogManager.getLogger('ReelProducer:ReelOutlineComposer');

type Props = {
  config: ReelProducerConfig;
  onOutlineChange: (outline: ReelOutline | null) => void;
  onStreamingChange?: (rawResponse: string, isLoading: boolean) => void;
};

const ReelOutlineComposer: React.FC<Props> = observer(
  ({ config, onOutlineChange, onStreamingChange }) => {
    const { outline, messages, isLoading, rawResponse, generate, sendMessage } =
      useReelOutlineGeneration();

    const hasTriggeredRef = useRef(false);

    // Trigger initial generation on mount
    useEffect(() => {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        logger.info('Triggering initial reel outline generation');
        generate(config);
      }
    }, [config, generate]);

    // Notify parent when outline changes
    useEffect(() => {
      onOutlineChange(outline);
    }, [outline, onOutlineChange]);

    // Notify parent of streaming state
    useEffect(() => {
      onStreamingChange?.(rawResponse, isLoading);
    }, [rawResponse, isLoading, onStreamingChange]);

    return (
      <Container>
        <ReelOutlineChatInterface
          messages={messages}
          onSendMessage={sendMessage}
          isLoading={isLoading}
        />
      </Container>
    );
  },
);

ReelOutlineComposer.displayName = 'ReelOutlineComposer';

export default ReelOutlineComposer;

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
